import axios from 'axios';
import axiosInstance from './axiosInstance';
import {
  ActualizarHijoRequest,
  ActualizarMiCuentaRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  HijoResponse,
  LoginRequest,
  LoginResponse,
  MessageResponse,
  MiCuentaInfo,
  RegisterRequest,
  ResetPasswordRequest,
  UserInfo,
  VincularHijoRequest,
} from '../types/auth.types';
import { getRefreshToken } from './tokenStore';

export async function register(data: RegisterRequest): Promise<MessageResponse> {
  const response = await axiosInstance.post<MessageResponse>('/api/auth/register', data);
  return response.data;
}

export async function login(data: LoginRequest): Promise<LoginResponse> {
  const response = await axiosInstance.post<LoginResponse>('/api/auth/login', data);
  return response.data;
}

export async function logout(): Promise<MessageResponse> {
  // El backend identifica la sesión a cerrar por el refresh token (no el access token),
  // por eso se envía explícitamente en lugar de dejar que el interceptor adjunte el access token.
  const refreshToken = getRefreshToken();
  const response = await axios.post<MessageResponse>(
    `${process.env.REACT_APP_API_URL}/api/auth/logout`,
    null,
    { headers: refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {} }
  );
  return response.data;
}

export async function refreshToken(refreshTokenValue: string): Promise<LoginResponse> {
  const response = await axiosInstance.post<LoginResponse>('/api/auth/refresh', {
    refreshToken: refreshTokenValue,
  });
  return response.data;
}

export async function changePassword(data: ChangePasswordRequest): Promise<MessageResponse> {
  const response = await axiosInstance.post<MessageResponse>('/api/auth/change-password', data);
  return response.data;
}

export async function forgotPassword(data: ForgotPasswordRequest): Promise<MessageResponse> {
  const response = await axiosInstance.post<MessageResponse>('/api/auth/forgot-password', data);
  return response.data;
}

// Estos dos endpoints son públicos y un 401 significa "token inválido/expirado", no "sesión
// vencida" — por eso usan axios directo en vez de axiosInstance, para no disparar el
// interceptor global que redirige a /login ante cualquier 401.
export async function resetPassword(data: ResetPasswordRequest): Promise<MessageResponse> {
  const response = await axios.post<MessageResponse>(
    `${process.env.REACT_APP_API_URL}/api/auth/reset-password`,
    data
  );
  return response.data;
}

export async function validarTokenReset(token: string): Promise<void> {
  await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/reset-password/validate`, {
    params: { token },
  });
}

export async function verifyEmail(token: string): Promise<MessageResponse> {
  const response = await axios.post<MessageResponse>(
    `${process.env.REACT_APP_API_URL}/api/auth/verify-email`,
    null,
    { params: { token } }
  );
  return response.data;
}

export async function resendVerification(email: string): Promise<MessageResponse> {
  const response = await axios.post<MessageResponse>(
    `${process.env.REACT_APP_API_URL}/api/auth/resend-verification`,
    { email }
  );
  return response.data;
}

export async function getMe(): Promise<UserInfo> {
  const response = await axiosInstance.get<UserInfo>('/api/auth/me');
  return response.data;
}

export async function getMiCuenta(): Promise<MiCuentaInfo> {
  const response = await axiosInstance.get<MiCuentaInfo>('/api/auth/mi-cuenta');
  return response.data;
}

export async function actualizarMiCuenta(data: ActualizarMiCuentaRequest): Promise<MiCuentaInfo> {
  const response = await axiosInstance.put<MiCuentaInfo>('/api/auth/mi-cuenta', data);
  return response.data;
}

export async function listarHijos(): Promise<HijoResponse[]> {
  const response = await axiosInstance.get<HijoResponse[]>('/api/auth/mi-cuenta/hijos');
  return response.data;
}

export async function vincularHijo(data: VincularHijoRequest): Promise<HijoResponse> {
  const response = await axiosInstance.post<HijoResponse>('/api/auth/mi-cuenta/hijos', data);
  return response.data;
}

export async function actualizarHijo(id: string, data: ActualizarHijoRequest): Promise<HijoResponse> {
  const response = await axiosInstance.put<HijoResponse>(`/api/auth/mi-cuenta/hijos/${id}`, data);
  return response.data;
}
